import { useState, useRef, useEffect } from "react"
import PageLayout from "components/layout/PageLayout"
import { useAskAiMutation, useGetAiStatusQuery } from "./aiApiSlice"
import { useTranslation } from "react-i18next"
import { escapeHtml, parseMarkdown } from "./utility"


// ── Sub-components ───────────────────────────────────────────────────────────

function ChatMessage({ message }) {
    const isUser = message.role === 'user'
    const isError = message.role === 'error'

    if (isUser) {
        return (
            <div className="d-flex justify-content-end mb-3">
                <div
                    className="bg-primary text-white rounded-3 px-3 py-2"
                    style={{ maxWidth: '75%', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}
                >
                    {message.text}
                </div>
            </div>
        )
    }

    return (
        <div className="d-flex justify-content-start mb-3">

            <div
                className={`rounded-3 px-3 py-2 ${isError ? 'bg-danger text-white' : 'bg-black bg-opacity-25'}`}
                style={{ maxWidth: '85%', wordBreak: 'break-word' }}
                dangerouslySetInnerHTML={{
                    __html: isError ? escapeHtml(message.text) : parseMarkdown(message.text)
                }}
            />
        </div>
    )
}

function TypingIndicator() {
    return (
        <div className="d-flex justify-content-start mb-3">
            <div className="bg-darker rounded-3 px-3 py-2 d-flex gap-1 align-items-center">
                <div className="spinner-grow spinner-grow-sm text-secondary" style={{ animationDelay: '0s' }} />
                <div className="spinner-grow spinner-grow-sm text-secondary" style={{ animationDelay: '0.15s' }} />
                <div className="spinner-grow spinner-grow-sm text-secondary" style={{ animationDelay: '0.3s' }} />
            </div>
        </div>
    )
}

function EmptyState() {
    const [t] = useTranslation(["global"])
    return (
        <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted">
            <div className="fs-1 mb-2"><i className="fa-solid fa-genderless fa-fade text-eh"></i></div>
            <p className="mb-0 fs-6">{t("helper.ask_your_ai_agent_question")}</p>
        </div>
    )
}

// ── Main component ───────────────────────────────────────────────────────────

export default function AiAgentUi() {
    const [t] = useTranslation(["ai"])
    const [askAi, { isLoading }] = useAskAiMutation()
    const {data:status, isLoading: isAiStatusLoading, isSuccess: isAiReady } = useGetAiStatusQuery()
    const [messages, setMessages] = useState([])
    const [input, setInput] = useState('')
    const messagesEndRef = useRef(null)


    useEffect(() => {
        if (isAiStatusLoading) {
            setMessages([])
            return
        }
        if (isAiReady)
            setMessages([{
                role: "ai",
                // text: `Ciao **${userFirstName}**, come posso aiutarti? 
                //         \n\n
                //         Sono il tuo assistente. Posso eseguire attività come \n
                //         💬 **Inviare** comunicazioni a più persone,
                //         🧠 **Riassumere** informazioni aziendali,
                //         ✔️ **Calcolare** statistiche rilevanti per il tuo lavoro
                //         `
                text : status.message
            }
            ])
        else setMessages([{
            role: "error",
            text: "L'agente AI non è momentaneamente disponibile. Riprova più tardi"

        }])
    }, [isAiStatusLoading])


    // Auto-scroll to bottom on new message or while loading
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [messages, isLoading])

    const handleSend = async () => {
        const trimmed = input.trim()
        if (!trimmed || isLoading) return

        setMessages(prev => [...prev, { role: 'user', text: trimmed }])
        setInput('')

        try {
            const result = await askAi({ input: trimmed }).unwrap()
            setMessages(prev => [...prev, { role: 'ai', text: result.output }])
        } catch (err) {
            const errMsg = err?.data?.message ?? err?.error ?? 'Errore imprevisto. Riprova.'
            setMessages(prev => [...prev, { role: 'error', text: errMsg }])
            setInput(trimmed)
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSend()
        }
    }

    const content = (
        <div className="d-flex flex-column " style={{ height: 'calc(75vh)' }}>

            {/* Messages area */}
            <div
                className="flex-grow-1 bg-opacity-25 rounded-3 p-3 mb-3 bg-dark"
                style={{ overflowY: 'auto' }}
            >
                {/* {messages.length === 0 && <EmptyState />} */}
                {messages.map((msg, idx) => (
                    <ChatMessage key={idx} message={msg} />
                ))}
                {(isLoading || isAiStatusLoading) && <TypingIndicator />}
                <div ref={messagesEndRef} />
            </div>

            {/* Input area */}
            <div className="input-group">
                <textarea
                    name="prompt"
                    className="form-control"
                    rows={1}
                    placeholder={t("helper.write_message")}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isLoading}
                    style={{ resize: 'none' }}
                />
                <button
                    className="btn btn-primary px-4"
                    onClick={handleSend}
                    disabled={isLoading || !input.trim()}
                    title="Invia"
                >
                    {isLoading
                        ? <span className="spinner-border spinner-border-sm" role="status" />
                        : '➤'
                    }
                </button>
            </div>

        </div>
    )

    return (
        <PageLayout
            pageTitle={t("menu.ai_agent")}
            bodyContent={content}
        />
    )
}
